import { useEffect, useState } from 'react';
import {
  useAddLink, useCandidates, useContactRoles, type ContactRole, type LinkTarget,
} from '../../api/contacts.ts';
import { Sheet, blank, field, hint, label, primary, secondary } from './Sheet.tsx';

type Kind = 'client' | 'facility' | 'property';

/**
 * Add a contact to a client, a building or a property: the role first, which decides what else is asked
 * (relationship for family, the account for a utility, "court appointed" for an appraiser or a broker);
 * then the person. As soon as a name, phone or email is typed, people already on record that the reader
 * can see are offered underneath: "Use this person" links them, "This is someone else" carries on. A
 * possible duplicate is a warning, never a refusal (two people can share a name).
 *
 * The court's people are added from People on the case, so Legal roles are not offered for a client here.
 */
export function AddContactSheet({ target, onClose }: { target: LinkTarget; onClose: () => void }) {
  const kind: Kind = 'client_id' in target ? 'client' : 'facility_id' in target ? 'facility' : 'property';
  const roles = useContactRoles();
  const add = useAddLink();
  const offered = (roles.data?.roles ?? []).filter((r) => attaches(r, kind) && !(kind === 'client' && r.category === 'Legal'));
  const [role, setRole] = useState('');
  const chosen = offered.find((r) => r.role_code === role) ?? null;
  const [p, setP] = useState({ last_name: '', first_name: '', phone_primary: '', email: '', job_title: '',
    street_address: '', unit: '', city: '', state: '', zip: '' });
  const [link, setLink] = useState({ relationship: '', is_primary: false, is_emergency: false, court_appointed: false, account_ref: '' });
  const [useId, setUseId] = useState<string | null>(null);
  // A candidate the person said is someone else stays dismissed; a new one, from what they type next, is offered.
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  // Asked after a pause in typing, never per keystroke.
  const [asked, setAsked] = useState({ name: null as string | null, phone: null as string | null, email: null as string | null });
  useEffect(() => {
    const t = setTimeout(() => {
      const name = [p.last_name, p.first_name].map((s) => s.trim()).filter(Boolean).join(', ');
      setAsked({ name: name.replace(/[^\p{L}]/gu, '').length >= 3 ? name : null, phone: blank(p.phone_primary), email: blank(p.email) });
    }, 500);
    return () => clearTimeout(t);
  }, [p.last_name, p.first_name, p.phone_primary, p.email]);
  const candidates = useCandidates(asked);
  const found = candidates.data?.candidates ?? [];
  const suggested = found.filter((c) => !dismissed.has(c.id));

  const family = chosen?.category === 'Family';
  const utility = chosen?.role_code === 'PROP_UTILITY';
  const appointable = chosen?.role_code === 'PROP_APPRAISER' || chosen?.role_code === 'PROP_REALTOR';
  const named = Boolean(p.last_name.trim() || p.first_name.trim());
  const ready = Boolean(chosen) && (useId !== null || named);

  const input = (k: keyof typeof p, title: string, type = 'text') => (
    <div>
      <label htmlFor={`ac-${k}`} className={label}>{title}</label>
      <input id={`ac-${k}`} type={type} className={field} value={p[k]} disabled={useId !== null}
             onChange={(e) => setP({ ...p, [k]: e.target.value })} />
    </div>
  );

  return (
    <Sheet labelId="add-contact-title" onClose={onClose} title="Add a contact">
      <form onSubmit={(e) => {
        e.preventDefault();
        const linkFields = {
          relationship: blank(link.relationship),
          is_primary: link.is_primary,
          is_emergency: link.is_emergency,
          ...(appointable ? { court_appointed: link.court_appointed } : {}),
          ...(utility ? { account_ref: blank(link.account_ref) } : {}),
        };
        add.mutate({
          ...target,
          role_code: role,
          ...(useId ? { contact_id: useId } : {
            person: Object.fromEntries(Object.entries(p).map(([k, v]) => [k, blank(v)])) as Record<string, string | null> & { last_name: string | null; first_name: string | null },
          }),
          link: linkFields,
        }, { onSuccess: onClose });
      }}>
        <label htmlFor="ac-role" className={label}>Role</label>
        <select id="ac-role" className={field} value={role} onChange={(e) => setRole(e.target.value)}>
          <option value="">Choose…</option>
          {groups(offered).map(([cat, rs]) => (
            <optgroup key={cat} label={cat}>
              {rs.map((r) => <option key={r.role_code} value={r.role_code}>{r.role_label}</option>)}
            </optgroup>
          ))}
        </select>
        {kind === 'client' && <p className={hint}>The court’s people (examiner, evaluator, attorneys) are added in People on the case.</p>}

        {useId ? (
          <div className="mt-3 rounded border border-line bg-tint px-3 py-2">
            <p className="text-neutral-900">Using {found.find((c) => c.id === useId)?.name ?? 'the person chosen'}, already on record.</p>
            <button type="button" className={`${secondary} mt-2`} onClick={() => setUseId(null)}>Someone else after all</button>
          </div>
        ) : (
          <>
            <div className="mt-3 grid gap-2 sm:grid-cols-2">
              {input('last_name', 'Surname')}
              {input('first_name', 'Given name')}
              {input('phone_primary', 'Phone', 'tel')}
              {input('email', 'Email', 'email')}
              {!family && input('job_title', 'Job title')}
            </div>
            {suggested.length > 0 && (
              <div className="mt-3 rounded border border-warning-edge bg-warning-bg px-3 py-2" role="status">
                <p className="font-semibold text-warning-fg">Already on record? People you can see with a matching name, phone or email:</p>
                <ul className="mt-1 divide-y divide-line">
                  {suggested.map((c) => (
                    <li key={c.id} className="flex flex-wrap items-center gap-2 py-1.5">
                      <span className="min-w-0 flex-1 text-neutral-900">
                        <span className="font-medium">{c.name}</span>
                        {c.organization && <span className="text-neutral-700"> · {c.organization}</span>}
                        <span className="block text-sm text-neutral-700">
                          Same {c.matched_on.join(' and ')}{c.phone ? ` · ${c.phone}` : ''}{c.email ? ` · ${c.email}` : ''}
                        </span>
                      </span>
                      <button type="button" className={secondary} onClick={() => setUseId(c.id)}>Use this person</button>
                    </li>
                  ))}
                </ul>
                <button type="button" className={`${secondary} mt-2`} onClick={() => setDismissed(new Set([...dismissed, ...suggested.map((c) => c.id)]))}>This is someone else</button>
              </div>
            )}
            {family && (
              <details className="mt-3">
                <summary className="min-h-[44px] cursor-pointer py-2 text-sm font-semibold text-neutral-800">Address (optional)</summary>
                <div className="grid gap-2 sm:grid-cols-2">
                  {input('street_address', 'Street')}
                  {input('unit', 'Unit')}
                  {input('city', 'City')}
                  {input('state', 'State')}
                  {input('zip', 'Zip')}
                </div>
              </details>
            )}
          </>
        )}

        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          {(family || kind === 'client') && (
            <div>
              <label htmlFor="ac-rel" className={label}>Relationship{family ? ' (daughter, nephew, friend…)' : ''}</label>
              <input id="ac-rel" className={field} value={link.relationship} maxLength={48}
                     onChange={(e) => setLink({ ...link, relationship: e.target.value })} />
            </div>
          )}
          {utility && (
            <div>
              <label htmlFor="ac-acct" className={label}>Account number</label>
              <input id="ac-acct" className={field} value={link.account_ref} maxLength={64}
                     onChange={(e) => setLink({ ...link, account_ref: e.target.value })} />
            </div>
          )}
        </div>
        <div className="mt-2 flex flex-wrap gap-x-4">
          <Box checked={link.is_primary} onChange={(v) => setLink({ ...link, is_primary: v })}>Primary for this role</Box>
          {kind === 'client' && <Box checked={link.is_emergency} onChange={(v) => setLink({ ...link, is_emergency: v })}>Emergency contact</Box>}
          {appointable && <Box checked={link.court_appointed} onChange={(v) => setLink({ ...link, court_appointed: v })}>Court appointed</Box>}
        </div>

        {add.error && <p role="alert" className="mt-2 rounded border border-warning-edge bg-warning-bg px-3 py-2 text-warning-fg">{add.error.message}</p>}
        <div className="mt-3 flex gap-2">
          <button type="submit" className={primary} disabled={!ready || add.isPending}>{add.isPending ? 'Adding…' : 'Add'}</button>
          <button type="button" className={secondary} onClick={onClose}>Cancel</button>
        </div>
      </form>
    </Sheet>
  );
}

function Box({ checked, onChange, children }: { checked: boolean; onChange: (v: boolean) => void; children: React.ReactNode }) {
  return (
    <label className="inline-flex min-h-[44px] items-center gap-2 text-sm text-neutral-800">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="h-5 w-5" />
      {children}
    </label>
  );
}

function attaches(r: ContactRole, kind: Kind): boolean {
  return kind === 'client' ? r.attaches_to_client : kind === 'facility' ? r.attaches_to_facility : r.attaches_to_property;
}

function groups(roles: ContactRole[]): [string, ContactRole[]][] {
  const out = new Map<string, ContactRole[]>();
  for (const r of roles) out.set(r.category, [...(out.get(r.category) ?? []), r]);
  return [...out.entries()];
}
